import {
  ContentHubType,
  AlternativeContentHubType,
  CollectionType,
  AlternativeCollectionType,
  LandingType,
  NO_URL_PREFIX_HOSTNAMES,
} from 'src/constants';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';

interface LessonUrlParams {
  lessonSlug: string;
  courseSlug?: string;
  pathSlug?: string;
  sectionSlug?: string;
  query?: string;
}

type MyLearningUrlParams = MyLearningPageContentSections | ':status';

export const LEARNING_PATH_URL_PATH = 'path';
export const COURSE_URL_PATH = 'course';
export const LESSON_URL_PATH = 'lesson';
export const TEAM_TRAINING_URL_PATH = 'team-training';

export const MY_LEARNING_URL_PATH = 'my-learning';
export const CATALOG_URL_PATH = 'catalog';

export const HUB_URL_PATH = 'hub';
export const COLLECTION_URL_PATH = 'collection';

export const LANDING_URL_PATH = '/landing';

export const LEARNING_PREFIX = '/learning';

// bifrost gateway url prefix - added by service descriptor during SSR (routing to bifrost gateway for HTML)
export const V1_ORIGIN_PREFIX = '/v1/origin';

// e.g. getTeamTrainingUrl(':teamTrainingId') -> /team-training/:teamTrainingId
export const getTeamTrainingUrl = (teamTrainingSlug: string): string => {
  return `/${TEAM_TRAINING_URL_PATH}/${teamTrainingSlug}`;
};

// e.g. withPrefix('/some/path') -> /learning/some/path (if tenant is community.atlassian.com)
export const withPrefix = (url: string, prefix?: string): string =>
  typeof prefix === 'string' ? `${prefix}${url}` : `${getUrlPrefix()}${url}`;

export const stripLeadingSlash = (url: string): string => (url.startsWith('/') ? url.slice(1) : url);

// e.g. getPathUrl(':pathId') -> /path/:pathId
// e.g. getPathUrl(':pathId', true) -> /path/:pathId/survey
// e.g. getPathUrl(':pathId', false, new URLSearchParams('q=1')) -> /path/:pathId?q=1
export const getPathUrl = (pathSlug: string, hasSurvey?: boolean, params?: URLSearchParams): string => {
  const queryParams = params ? `?${params}` : '';

  return `/${LEARNING_PATH_URL_PATH}/${pathSlug}${hasSurvey ? '/survey' : ''}${queryParams}`;
};

// e.g. getCourseUrl(':courseId') -> /course/:courseId
// e.g. getCourseUrl(':courseId', ':pathId') -> /path/:pathId/course/:courseId
// e.g. getCourseUrl(':courseId', '', true) -> /course/:courseId/survey
// e.g. getCourseUrl(':courseId', ':pathId', true) -> /path/:pathId/course/:courseId/survey
// e.g. getCourseUrl(':courseId', undefined, false, new URLSearchParams('foo=bar')) -> /course/:courseId?foo=bar
export const getCourseUrl = (
  courseSlug: string,
  pathSlug?: string,
  hasSurvey?: boolean,
  params?: URLSearchParams,
): string => {
  const learningPathUrl = pathSlug ? getPathUrl(pathSlug) : '';
  const queryParams = params ? `?${params}` : '';

  return `${learningPathUrl}/${COURSE_URL_PATH}/${courseSlug}${hasSurvey ? '/survey' : ''}${queryParams}`;
};

// e.g. getLessonUrl({ lessonSlug: ':lessonId' }) -> /lesson/:lessonId
// e.g. getLessonUrl({ courseSlug: ':courseId', lessonSlug: ':lessonId' }) -> /course/:courseId/lesson/:lessonId
// e.g. getLessonUrl({ pathSlug: ':pathId', courseSlug: ':courseId', lessonSlug: ':lessonId' }) -> /path/:pathId/course/:courseId/lesson/:lessonId
// e.g. getLessonUrl({ lessonSlug: ':lessonId', sectionSlug: 'quiz' }) -> /lesson/:lessonId#quiz
// e.g. getLessonUrl({ lessonSlug: ':lessonId', query: 'foo=bar' }) -> /lesson/:lessonId?foo=bar
export const getLessonUrl = ({ lessonSlug, courseSlug, pathSlug, sectionSlug, query }: LessonUrlParams): string => {
  const courseUrl = courseSlug ? getCourseUrl(courseSlug, pathSlug) : '';
  const sectionFragment = sectionSlug ? `#${sectionSlug}` : '';
  const queryFragment = query ? `?${query}` : '';

  return `${courseUrl}/${LESSON_URL_PATH}/${lessonSlug}${queryFragment}${sectionFragment}`;
};

// e.g. getHubUrl(ContentHubType.LEARNING_PATHS) -> /hub/all-learning-paths
// e.g. getHubUrl(ContentHubType.LEARNING_PATHS, 'section1') -> /hub/all-learning-paths#section1
// e.g. getHubUrl(ContentHubType.LEARNING_PATHS, undefined, 'some-hub') -> /hub/all-learning-paths/some-hub
// e.g. getHubUrl(ContentHubType.LEARNING_PATHS, undefined, 'some-hub', true) -> /hub/all-learning-paths/some-hub/*
export const getHubUrl = (
  type: ContentHubType | AlternativeContentHubType,
  sectionId?: string,
  hubSlug?: string,
  wildcard?: boolean,
): string => {
  const hubFragment = hubSlug ? `/${hubSlug}` : '';
  const wildcardFragment = wildcard ? '/*' : '';
  const sectionFragment = sectionId ? `#${sectionId}` : '';

  return `/${HUB_URL_PATH}/${type}${hubFragment}${wildcardFragment}${sectionFragment}`;
};

// e.g. getCollectionUrl(CollectionType.TOPIC, ':topicId') -> /collection/topic/:topicId
export const getCollectionUrl = (type: CollectionType | AlternativeCollectionType, collectionSlug?: string): string => {
  const collectionFragment = collectionSlug ? `/${collectionSlug}` : '';

  return `/${COLLECTION_URL_PATH}/${type}${collectionFragment}`;
};

// e.g. getCertificationUrl() -> /certifications
// e.g. getCertificationUrl(':certificationId') -> /certifications/:certificationId
export const getCertificationUrl = (certificationSlug?: string): string => {
  const certificationFragment = certificationSlug ? `/${certificationSlug}` : '';

  return `/${ContentHubType.CERTIFICATIONS}${certificationFragment}`;
};

// e.g. getCatalogUrl() -> /catalog
// e.g. getCatalogUrl('/filter') -> /catalog/filter
export const getCatalogUrl = (filter?: string): string => `/${CATALOG_URL_PATH}${filter || ''}`;

// e.g. getLandingUrl(LandingType.LIVE_TRAINING) -> /live-training
// e.g. withPrefix(getLandingUrl(LandingType.LIVE_TRAINING), LANDING_URL_PATH) -> /landing/live-training
export const getLandingUrl = (type: LandingType, slug?: string): string => {
  const landingFragment = slug ? `/${slug}` : '';

  return `/${type}${landingFragment}`;
};

// e.g. getMyLearningUrl(':status') -> /my-learning/:status
export const getMyLearningUrl = (status: MyLearningUrlParams): string => `/${MY_LEARNING_URL_PATH}/${status}`;

/**
 * Determines the URL prefix based on the current hostname, critical for proper routing and URL generation.
 *
 * @returns The URL prefix string:
 *   - Returns "" (empty string) for hostnames in NO_URL_PREFIX_HOSTNAMES
 *   - Returns "/learning" by default (Community Learning)
 */
export const getUrlPrefix = (): string => {
  const hostname = globalThis.location?.hostname;

  if (hostname && NO_URL_PREFIX_HOSTNAMES.includes(hostname)) {
    return '';
  }

  return LEARNING_PREFIX;
};

/**
 * Normalizes the URL for routing by removing the /v1/origin prefix, fixing double slashes,
 * replacing /index.html with /, and adding the url prefix (eg. '/learning') if it doesn't exist.
 *
 * @param url - The URL to normalize.
 * @returns The normalized URL.
 */
export const normalizeUrlForRouting = (url: URL): string => {
  const normalizedUrl = new URL(url.href);
  let pathname = normalizedUrl.pathname;

  if (!pathname) {
    return normalizedUrl.href;
  }

  // Remove /v1/origin prefix that gets added by service descriptor during SSR (routing to bifrost gateway)
  // This is only added for deployed environments for non-community domains, not added when running locally - interferes with SSR in-app react routing when present
  if (pathname.startsWith(`${V1_ORIGIN_PREFIX}/`)) {
    pathname = pathname.slice(V1_ORIGIN_PREFIX.length);
  }

  // Fix double slashes that occur when running locally (e.g. //some/path -> /some/path)
  if (pathname.startsWith('//')) {
    pathname = pathname.substring(1);
  }

  // Replace /index.html with / (tesseract requests /index.html route instead of '/')
  if (pathname.endsWith('/index.html') || pathname === 'index.html') {
    pathname = pathname.replace('/index.html', '/');
  }

  // Add url prefix (eg. '/learning') if it doesn't exist
  const urlPrefix = getUrlPrefix();

  if (urlPrefix && !pathname.startsWith(urlPrefix)) {
    pathname = withPrefix(pathname);
  }

  normalizedUrl.pathname = pathname;

  // Clean up trailing characters '?' and '/' and return
  return normalizedUrl.href
    .replace(/\?$/, '') // Remove trailing ?
    .replace(/\/$/, ''); // Remove trailing /
};

export const prepareUrlForAuth = (): string => {
  const url = new URL(globalThis.location.href);

  return normalizeUrlForRouting(url);
};
